import { useRef, useState } from "react";
import { motion, useMotionValue, useSpring, AnimatePresence } from "framer-motion";
import { Play, X } from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { useContent } from "@/hooks/useContent";

const embedUrl = (trackId) =>
  `https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/tracks/${trackId}&color=%23f59e0b&auto_play=true&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false`;

function SetCard({ set, index, onPlay }) {
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 160, damping: 18 });
  const sry = useSpring(ry, { stiffness: 160, damping: 18 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 10);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 10);
  };
  const onLeave = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <Reveal delay={(index % 3) * 0.08}>
      <motion.div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        style={{ rotateX: srx, rotateY: sry, transformStyle: "preserve-3d" }}
        data-testid={`set-card-${set.id}`}
        className="group relative border border-[#232638] hover:border-amber-400/50 bg-[#10121A] transition-colors duration-500"
      >
        <div className="relative overflow-hidden">
          <img
            src={set.cover}
            alt={set.title}
            loading="lazy"
            className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-[#08090C]/0 group-hover:bg-[#08090C]/45 transition-colors duration-500 flex items-center justify-center">
            <button
              data-testid={`set-card-play-button-${set.id}`}
              onClick={() => onPlay(set)}
              aria-label={`Ouvir ${set.title}`}
              className="opacity-0 group-hover:opacity-100 scale-75 group-hover:scale-100 transition-all duration-500 w-16 h-16 rounded-full bg-amber-400 text-[#08090C] flex items-center justify-center shadow-[0_0_40px_rgba(245,158,11,0.4)]"
            >
              <Play size={24} className="ml-1" />
            </button>
          </div>
          <span className="absolute top-3 left-3 font-mono text-[10px] tracking-[0.2em] bg-[#08090C]/80 backdrop-blur px-2.5 py-1 text-amber-400">
            {set.year}
          </span>
        </div>
        <div className="p-5 flex items-center justify-between gap-3">
          <h3 className="font-display text-lg font-bold tracking-tight uppercase">{set.title}</h3>
          <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 shrink-0">{set.genre}</span>
        </div>
      </motion.div>
    </Reveal>
  );
}

const SetsGrid = () => {
  const { data: sets } = useContent("/content/sets.json", []);
  const [active, setActive] = useState(null);

  return (
    <section id="sets" data-testid="sets-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
      <SectionHeading
        chapter="03 // Sets & álbuns"
        title="Arquivo de pista"
        note="Passe o mouse e aperte o play — cada capa abre o set direto do SoundCloud."
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 [perspective:1200px]">
        {sets.map((set, i) => (
          <SetCard key={set.id} set={set} index={i} onPlay={setActive} />
        ))}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[#08090C]/90 backdrop-blur-md flex items-center justify-center p-4"
            onClick={() => setActive(null)}
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="w-full max-w-2xl border border-[#3F4462] bg-[#10121A] p-5 sm:p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-400">{active.genre} — {active.year}</p>
                  <h3 className="font-display text-xl font-bold uppercase tracking-tight mt-1">{active.title}</h3>
                </div>
                <button
                  data-testid="set-player-modal-close"
                  onClick={() => setActive(null)}
                  aria-label="Fechar player"
                  className="w-10 h-10 rounded-full border border-[#3F4462] flex items-center justify-center text-zinc-400 hover:text-amber-400 hover:border-amber-400/60 transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
              <iframe
                title={active.title}
                data-testid="soundcloud-player-iframe"
                width="100%"
                height="166"
                scrolling="no"
                frameBorder="no"
                allow="autoplay"
                src={embedUrl(active.soundcloudTrackId)}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default SetsGrid;
